import { formatDecimalNumber } from "@vericus/cadmus-common";
import {
  ScoreBreakdownCardProps,
  ScoreRow,
  ScoreSection,
} from "@vericus/cadmus-ui";

import type {
  ComponentScore as GqlComponentScore,
  PeerOutcome as GqlPeerOutcome,
} from "@/generated/graphql";

/**
 * Per-component score shape, derived from the generated GraphQL `ComponentScore`
 * so it stays in sync with the schema — picking only the fields this mapper reads.
 */
type ComponentScore = Pick<
  GqlComponentScore,
  | "score"
  | "maxScore"
  | "weightedScore"
  | "weightedMaxScore"
  | "modifiersTotal"
  | "moderatorModifier"
  | "penaltyScore"
> & {
  // Widened to nullable: the mapper treats an absent weight as "hide the
  // weighting label" (see `weightPct`), which the schema's non-null type
  // can't represent.
  weight: GqlComponentScore["weight"] | null;
};

type PeerOutcome = Pick<
  GqlPeerOutcome,
  "isComplete" | "groupReportScaleFactor"
>;

/** Per-component inputs — each taken from its own release-specific breakdown. */
export interface ScoreBreakdownInputs {
  group?: ComponentScore | null;
  individual?: ComponentScore | null;
  peer?: ComponentScore | null;
  self?: ComponentScore | null;
  total: {
    score: number | null;
    maxScore: number | null;
    moderatorModifierStudent: number | null;
  };
}

export interface ReleaseFlags {
  group: boolean;
  individual: boolean;
  peer: boolean;
  self: boolean;
}

/**
 * Format via the shared `formatDecimalNumber` so teacher (athena) and student
 * (aphrodite) render identically (2 dp, trailing zeros dropped). Null (not yet
 * scored) renders as a dash so it can't be confused with a genuine zero.
 */
function fmt(n: number | null | undefined): string {
  if (n == null) return "–";
  return formatDecimalNumber(n);
}

/** Format an adjustment delta: negatives keep their leading "-"; positives are
 * shown plain (no leading "+"). */
function signed(n: number): string {
  return fmt(n);
}

/** `weight` is already a 0-100 percentage; round for display. Null (weight
 * not configured) passes through so the card hides the weighting label. */
function weightPct(weight: number | null): number | undefined {
  return weight == null ? undefined : Math.round(weight * 100) / 100;
}

/** Build the per-line rows for a weighted component (group / individual).
 * `score` already includes the moderator adjustment, so the teacher-awarded
 * base (score − moderator) is shown separately from the moderator row. */
function componentRows(c: ComponentScore): ScoreRow[] {
  const modifiers = c.modifiersTotal ?? 0;
  const moderator = c.moderatorModifier ?? 0;
  const penalty = c.penaltyScore ?? 0;
  const rows: ScoreRow[] = [
    {
      label: "Base score",
      sublabel: `(out of ${c.maxScore ?? 0})`,
      value: c.score == null ? fmt(null) : fmt(c.score - moderator),
    },
  ];
  // GraphQL exposes one modifiers total, not the Figma "everyone/you" split.
  if (modifiers !== 0)
    rows.push({ label: "Adjusted by marker", value: signed(modifiers) });
  if (moderator !== 0)
    rows.push({ label: "Adjusted by moderator", value: signed(moderator) });
  if (penalty !== 0)
    rows.push({ label: "Penalties", value: signed(-Math.abs(penalty)) });
  return rows;
}

/** Un-weighted component subtotal. `score` already includes the moderator
 * adjustment, so this is score + marker modifiers − penalty (equivalently
 * base + marker + moderator − penalty). Matches the backend weighted score.
 * Null base score → null so it renders as a dash, not a misleading 0. */
function unweighted(c: ComponentScore): number | null {
  if (c.score == null) return null;
  return c.score + (c.modifiersTotal ?? 0) - (c.penaltyScore ?? 0);
}

/** A released component section with its weighted score + detail rows.
 * A 0%-weight component is display-only (D5): collapse it to "-- / --" with
 * no detail rows, regardless of release state — see call sites below. */
function componentSection(
  label: string,
  noun: string,
  c: ComponentScore
): ScoreSection {
  if (c.weight === 0) {
    return {
      label,
      weight: weightPct(c.weight),
      score: { notApplicable: true },
    };
  }
  return {
    label,
    weight: weightPct(c.weight),
    score: {
      value: fmt(c.weightedScore),
      max: c.weightedMaxScore ?? undefined,
    },
    rows: componentRows(c),
    subtotal: [
      { label: `Un-weighted ${noun} score`, value: fmt(unweighted(c)) },
    ],
  };
}

/** A component whose feedback hasn't been released to the student yet. */
function pendingSection(label: string, weight: number | null): ScoreSection {
  return { label, weight: weightPct(weight), score: { pending: true } };
}

/**
 * Choose the display section for a component. A 0%-weight component is always
 * the display-only D5 section; otherwise it's the released section once `ready`
 * and Pending until then. Keeps the D5 weight-0 override in one place across
 * the group/individual/peer/self sections.
 */
function sectionFor(
  label: string,
  noun: string,
  c: ComponentScore,
  ready: boolean
): ScoreSection {
  return c.weight === 0 || ready
    ? componentSection(label, noun, c)
    : pendingSection(label, c.weight);
}

/** Map a student's per-component scores into `ScoreBreakdownCard` props. */
export function toScoreBreakdownProps(
  inputs: ScoreBreakdownInputs,
  peerOutcome: PeerOutcome | null,
  released: ReleaseFlags,
  opts: { peerScalingMode: boolean; showPeerScores: boolean }
): Pick<ScoreBreakdownCardProps, "total" | "sections"> {
  const sections: ScoreSection[] = [];

  if (inputs.group != null) {
    sections.push(
      sectionFor("Group Work", "group work", inputs.group, released.group)
    );
  }

  if (inputs.individual != null) {
    sections.push(
      sectionFor(
        "Individual Work",
        "individual work",
        inputs.individual,
        released.individual
      )
    );
  }

  // Peer/self: Pending unless released, complete, and a weighted score exists.
  // A genuine 0 is a valid score (renders "0"); only null (not yet scored)
  // falls back to Pending.
  const peerComplete = peerOutcome?.isComplete === true;

  if (opts.peerScalingMode) {
    const factor = peerOutcome?.groupReportScaleFactor;
    sections.push({
      label: "Peer Evaluation",
      meta: "Group score multiplier",
      score:
        released.peer && peerComplete && factor != null
          ? { value: fmt(factor) }
          : { pending: true },
    });
  } else if (inputs.peer != null) {
    if (!opts.showPeerScores) {
      // Peer scores withheld from the student: show "xx" instead of the value
      // (mirrors FeedbackItem's gate) and omit the rows that would reveal it —
      // unless the weight is 0, which is not-applicable regardless (D5).
      sections.push(
        inputs.peer.weight === 0
          ? componentSection("Peer Evaluation", "peer evaluation", inputs.peer)
          : {
              label: "Peer Evaluation",
              weight: weightPct(inputs.peer.weight),
              score: {
                value: "xx",
                max: inputs.peer.weightedMaxScore ?? undefined,
              },
            }
      );
    } else {
      const peerReady =
        released.peer && peerComplete && inputs.peer.weightedScore != null;
      sections.push(
        sectionFor("Peer Evaluation", "peer evaluation", inputs.peer, peerReady)
      );
    }
  }

  // Self releases with the group report; suppressed in peer scaling mode.
  if (!opts.peerScalingMode && inputs.self != null) {
    const selfReady =
      released.self && peerComplete && inputs.self.weightedScore != null;
    sections.push(
      sectionFor("Self Evaluation", "self evaluation", inputs.self, selfReady)
    );
  }

  // Student-level moderator adjustment — the only moderation folded into the
  // final grade. Per-component moderator modifiers are reported by the backend
  // but NOT applied to the total score, so they are not shown here.
  const overallAdjustment = inputs.total.moderatorModifierStudent ?? 0;
  if (overallAdjustment !== 0) {
    sections.push({
      label: "Overall adjustments",
      pill: { value: signed(overallAdjustment) },
      rows: [
        {
          label: "Adjusted by moderator on final grade",
          value: signed(overallAdjustment),
        },
      ],
    });
  }

  return {
    total: { score: fmt(inputs.total.score), max: inputs.total.maxScore ?? 0 },
    sections,
  };
}
